import { Badge } from "@/components/ui/badge";
import { DashboardErrorTile } from "@/components/dashboard/DashboardErrorTile";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import type {
  ActivityItem,
  AdminRole,
  CareQueueItem,
  CoachActivityItem,
  CoachContentItem,
  CoachManagerStats,
  ContentItem,
  ContentManagerStats,
  DashboardStats,
  RevenueChannelsData,
  RitualFlowData,
  SubscriptionHealthData,
} from "@/lib/schema";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  ArrowRight,
  Banknote,
  BarChart3,
  BookOpen,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileCheck,
  FileText,
  Heart,
  Maximize2,
  Minus,
  Plus,
  Smartphone,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

import {
  ContentOverviewData,
  dashboardService,
} from "@/lib/api/dashboard.service";
import { getPrimaryRole } from "@/lib/permissions";
import {
  canViewSection,
  getRoleConfig,
  type DashboardSection,
} from "@/lib/roleConfig";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const urgencyConfig = {
  high: {
    dot: "bg-rose-500",
    text: "text-rose-600 dark:text-rose-400",
    label: "urgent",
  },
  medium: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
    label: "soon",
  },
  low: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
    label: "low",
  },
};

const activityIcons: Record<string, React.ElementType> = {
  content_published: FileText,
  coach_added: UserPlus,
  system_alert: AlertCircle,
  content_review: FileCheck,
};

export default function Dashboard() {
  const { user } = useAuth();
  const currentRole = (getPrimaryRole(user?.roles ?? []) ??
    "super_admin") as AdminRole;
  const roleConfig = getRoleConfig(currentRole);

  const [channelsPeriod, setChannelsPeriod] = useState<string>("7days");
  const [channelsFullscreen, setChannelsFullscreen] = useState<
    null | "mobile" | "affiliate" | "commissions"
  >(null);

  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = useQuery<DashboardStats>({
    queryKey: ["dashboard", "stats"],
    // Content counts change on approve/submit/publish; refetch on return (EVO-50).
    refetchOnMount: "always",
    queryFn: dashboardService.stats,
  });

  const { data: myContent, isLoading: myContentLoading } = useQuery<
    CoachContentItem[]
  >({
    queryKey: ["dashboard", "my-content"],
    refetchOnMount: "always",
    queryFn: dashboardService.myContent,
    enabled: currentRole === "coach",
  });

  const { data: activities, isLoading: activitiesLoading } = useQuery<
    ActivityItem[]
  >({
    queryKey: ["dashboard", "activities"],
    queryFn: dashboardService.activities,
    enabled: canViewSection(currentRole, "recent_activity"),
  });

  const { data: ritualFlow, isLoading: ritualFlowLoading } =
    useQuery<RitualFlowData>({
      queryKey: ["/api/dashboard/ritual-flow"],
      enabled: false, // section removed (Weekly Engagement Flow + Ritual Metrics)
    });

  const { data: careQueue, isLoading: careQueueLoading } = useQuery<
    CareQueueItem[]
  >({
    queryKey: ["/api/dashboard/care-queue"],
    enabled: false, // section removed (Care Queue)
  });

  const {
    data: subscriptionHealth,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
    refetch: refetchSubscriptionHealth,
  } = useQuery<SubscriptionHealthData>({
    queryKey: ["dashboard", "subscription-health"],
    queryFn: dashboardService.subscriptionHealth,
    enabled: roleConfig.canViewSubscriptions,
  });

  // Revenue & Channels (mobile / affiliate / commissions)
  const { data: channelsData, isLoading: channelsLoading } =
    useQuery<RevenueChannelsData>({
      queryKey: ["dashboard", "revenue-channels", channelsPeriod],
      queryFn: () =>
        dashboardService.revenueChannels(
          channelsPeriod as "7days" | "30days" | "alltime",
        ),
      enabled: roleConfig.canViewRevenue && currentRole === "super_admin",
    });

  const canView = (section: DashboardSection) =>
    canViewSection(currentRole, section);

  const {
    data: contentOverview,
    isLoading: contentOverviewLoading,
    error: contentOverviewError,
    refetch: refetchContentOverview,
  } = useQuery<ContentOverviewData>({
    queryKey: ["dashboard", "content-overview"],
    refetchOnMount: "always",
    queryFn: dashboardService.contentOverview,
    enabled: canView("content_studio_overview"),
  });

  const TrendBadge = ({
    value,
    suffix = "%",
  }: {
    value: number;
    suffix?: string;
  }) => {
    const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : Minus;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-xs font-medium",
          value > 0
            ? "text-emerald-600 dark:text-emerald-400"
            : value < 0
              ? "text-rose-500"
              : "text-muted-foreground",
        )}
      >
        <Icon className="h-3 w-3" />
        {Math.abs(value)}
        {suffix}
      </span>
    );
  };

  const sortedCareQueue = careQueue
    ? [...careQueue].sort((a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return order[a.urgency] - order[b.urgency];
      })
    : [];

  const contentManagerStats: ContentManagerStats = {
    publishedThisWeek: 8,
    coachSubmissions: 5,
    contentGaps: 3,
  };

  const coachManagerStats: CoachManagerStats = {
    activeCoaches: 24,
    onboardingCoaches: 3,
    pendingCoachSubmissions: 7,
    coachesNeedingAttention: 2,
  };

  const pendingContent: ContentItem[] = [
    {
      id: "1",
      title: "Morning Mindfulness Ritual",
      type: "audio",
      coachName: "Sarah Chen",
      submittedAt: "2026-01-06T10:00:00Z",
      status: "pending_review",
    },
    {
      id: "2",
      title: "Stress Relief Journey",
      type: "journey",
      coachName: "Maria Santos",
      submittedAt: "2026-01-05T14:30:00Z",
      status: "pending_review",
    },
    {
      id: "3",
      title: "Evening Wind-Down",
      type: "audio",
      coachName: "Emily Rose",
      submittedAt: "2026-01-05T09:15:00Z",
      status: "pending_review",
    },
  ];

  const coachActivity: CoachActivityItem[] = [
    {
      id: "1",
      coachId: "mock-1",
      coachName: "Sarah Chen",
      action: "Submitted new audio content",
      timestamp: "2026-01-07T08:00:00Z",
      status: "active",
    },
    {
      id: "2",
      coachId: "mock-2",
      coachName: "New Coach",
      action: "Completed onboarding step 2",
      timestamp: "2026-01-07T07:30:00Z",
      status: "onboarding",
    },
    {
      id: "3",
      coachId: "mock-3",
      coachName: "Maria Santos",
      action: "No activity in 14 days",
      timestamp: "2026-01-06T12:00:00Z",
      status: "needs_attention",
    },
  ];

  const getMetricValue = (dataKey: string): number | string => {
    switch (dataKey) {
      case "totalActiveUsers":
        return stats?.totalActiveUsers || 0;
      case "averageEngagement":
        return stats?.averageEngagement || 0;
      case "activeMemberships":
        return subscriptionHealth?.activeMemberships || 0;
      case "monthlySupportValue":
        // MRR in plain dollars, same figure and format as the Subscriptions page (EVO-110).
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(subscriptionHealth?.monthlySupportValue ?? 0);
      case "trialsEnding":
        return stats?.trialsEnding || 0;
      case "membersNeedingSupport":
        return stats?.membersNeedingSupport || 0;
      case "publishedThisWeek":
        return contentManagerStats.publishedThisWeek;
      case "coachSubmissions":
        return contentManagerStats.coachSubmissions;
      case "contentGaps":
        return contentManagerStats.contentGaps;
      case "activeCoaches":
        return coachManagerStats.activeCoaches;
      case "onboardingCoaches":
        return coachManagerStats.onboardingCoaches;
      case "pendingCoachSubmissions":
        return coachManagerStats.pendingCoachSubmissions;
      case "coachesNeedingAttention":
        return coachManagerStats.coachesNeedingAttention;
      case "myDrafts":
        return stats?.myDrafts ?? 0;
      case "myPublished":
        return stats?.myPublished ?? 0;
      case "pendingApproval":
        return stats?.pendingApproval ?? 0;
      case "totalViews":
        return stats?.totalViews ?? 0;
      case "followers":
        return stats?.followers ?? 0;
      case "referralSignups":
        return stats?.referralSignups ?? 0;
      case "paidReferrals":
        return stats?.paidReferrals ?? 0;
      case "commissionEarned":
        return `$${(stats?.commissionEarned ?? 0).toFixed(2)}`;
      default:
        return 0;
    }
  };

  // A failed source request must not render as a real 0 (EVO-52): return the
  // retry for the tile's source query when that query errored.
  const getMetricRetry = (dataKey: string): (() => void) | null => {
    switch (dataKey) {
      case "totalActiveUsers":
      case "averageEngagement":
      case "trialsEnding":
      case "membersNeedingSupport":
      case "myDrafts":
      case "myPublished":
      case "pendingApproval":
      case "totalViews":
      case "followers":
      case "referralSignups":
      case "paidReferrals":
      case "commissionEarned":
        return statsError ? () => refetchStats() : null;
      case "activeMemberships":
        return subscriptionError ? () => refetchSubscriptionHealth() : null;
      case "monthlySupportValue":
        return subscriptionError ? () => refetchSubscriptionHealth() : null;
      default:
        return null;
    }
  };

  const getTrendValue = (trendKey?: string): number | null | undefined => {
    if (!trendKey) return undefined;
    switch (trendKey) {
      case "totalActiveUsersTrend":
        return stats?.totalActiveUsersTrend;
      case "averageEngagementTrend":
        return stats?.averageEngagementTrend;
      case "activeMembershipsTrend":
        return subscriptionHealth?.activeMembershipsTrend;
      case "monthlySupportValueTrend":
        return subscriptionHealth?.monthlySupportValueTrend;
      default:
        return undefined;
    }
  };

  const getRoleActions = () => {
    switch (currentRole) {
      case "super_admin":
      case "admin":
        return (
          <div className="flex items-center gap-2">
            <Link href="/coaches" data-testid="link-invite-coach">
              <Button variant="outline" size="sm" className="gap-2">
                <UserPlus className="h-4 w-4" />
                Invite Coach
              </Button>
            </Link>
            <Link href="/studio" data-testid="link-create-content">
              <Button size="sm" className="gap-2">
                <FileText className="h-4 w-4" />
                Create Content
              </Button>
            </Link>
          </div>
        );
      case "coach":
        return (
          <div className="flex items-center gap-2">
            <Link href="/studio" data-testid="link-create-content-coach">
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Create New
              </Button>
            </Link>
          </div>
        );
      case "coach_manager":
        return (
          <div className="flex items-center gap-2">
            <Link href="/coaches" data-testid="link-manage-coaches">
              <Button size="sm" className="gap-2">
                <UserCheck className="h-4 w-4" />
                Manage Coaches
              </Button>
            </Link>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen p-6 lg:p-8 space-y-6">
      {/* Page Header with Role Context */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight"
            data-testid="text-dashboard-title"
          >
            {roleConfig.title}
          </h1>
          <p
            className="text-muted-foreground mt-1"
            data-testid="text-dashboard-subtitle"
          >
            {roleConfig.heroMessage}
          </p>
        </div>
        {getRoleActions()}
      </div>

      {/* ===== DYNAMIC METRIC CARDS ===== */}
      {canView("metric_cards") && (
        <section data-testid="section-metrics">
          <div
            className={cn(
              "grid gap-4",
              roleConfig.metricCards.length === 5
                ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
                : roleConfig.metricCards.length === 4
                  ? "grid-cols-2 md:grid-cols-4"
                  : "grid-cols-2 md:grid-cols-3",
            )}
          >
            {roleConfig.metricCards.map((card) => {
              const Icon = card.icon;
              const value = getMetricValue(card.dataKey);
              const trend = getTrendValue(card.trendKey);
              const retry = getMetricRetry(card.dataKey);

              return (
                <Card key={card.id} data-testid={`card-metric-${card.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className={cn("p-2 rounded-lg", card.iconBg)}>
                        <Icon className={cn("h-4 w-4", card.iconColor)} />
                      </div>
                      {!retry && trend !== undefined && trend !== null && (
                        <TrendBadge value={trend} />
                      )}
                    </div>
                    {statsLoading || subscriptionLoading ? (
                      <div className="h-8 w-20 bg-muted/40 rounded animate-pulse" />
                    ) : retry ? (
                      <p
                        className="text-2xl font-semibold text-muted-foreground"
                        data-testid={`text-metric-${card.id}`}
                      >
                        —
                      </p>
                    ) : (
                      <p
                        className="text-2xl font-semibold text-foreground"
                        data-testid={`text-metric-${card.id}`}
                      >
                        {typeof value === "number"
                          ? value.toLocaleString()
                          : value}
                        {card.suffix || ""}
                        {card.format === "percent" ? "%" : ""}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {card.title}
                    </p>
                    {retry && (
                      <button
                        type="button"
                        onClick={retry}
                        className="mt-1 text-xs text-destructive underline-offset-2 hover:underline"
                        data-testid={`button-retry-metric-${card.id}`}
                      >
                        Couldn't load · Retry
                      </button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ===== COACH: MY CONTENT ===== */}
      {canView("my_content") && (
        <section data-testid="section-my-content">
          <Card data-testid="card-my-content">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/15 to-rose-500/15 dark:from-violet-500/25 dark:to-rose-500/25">
                    <FileText className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-serif font-semibold tracking-tight">
                      My Content
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Everything you've created, in one place
                    </p>
                  </div>
                </div>
                <Link href="/studio" data-testid="link-open-studio">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 hover-elevate"
                  >
                    Open Studio
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {myContentLoading ? (
                <div className="space-y-2" data-testid="my-content-loading">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-14 rounded-md bg-muted/40 animate-pulse"
                    />
                  ))}
                </div>
              ) : !myContent || myContent.length === 0 ? (
                <div
                  className="text-center py-8 border border-dashed rounded-md"
                  data-testid="my-content-empty"
                >
                  <p className="text-sm text-muted-foreground">
                    You haven't created any content yet.
                  </p>
                  <Link href="/studio">
                    <Button
                      size="sm"
                      className="mt-3 gap-2"
                      data-testid="button-create-first-content"
                    >
                      <Plus className="h-4 w-4" />
                      Create your first piece
                    </Button>
                  </Link>
                </div>
              ) : (
                <ul className="divide-y" data-testid="list-my-content">
                  {myContent.slice(0, 8).map((item) => (
                    <li
                      key={`${item.contentType}-${item.id}`}
                      className="flex items-center justify-between gap-3 py-3"
                      data-testid={`row-my-content-${item.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="text-sm font-medium text-foreground truncate"
                          data-testid={`text-content-title-${item.id}`}
                        >
                          {item.title}
                        </p>
                        <p
                          className="text-xs text-muted-foreground mt-0.5"
                          data-testid={`text-content-meta-${item.id}`}
                        >
                          {item.contentType} · updated{" "}
                          {formatDistanceToNow(new Date(item.updatedAt), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className="capitalize"
                        data-testid={`badge-content-status-${item.id}`}
                      >
                        {item.status.replace(/_/g, " ")}
                      </Badge>
                      <Link href={item.editUrl}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5"
                          data-testid={`button-edit-content-${item.id}`}
                        >
                          Edit
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== COACH: MY PROFILE ===== */}
      {canView("my_profile") && (
        <section data-testid="section-my-profile">
          <Card data-testid="card-my-profile">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500/15 to-teal-500/15 dark:from-emerald-500/25 dark:to-teal-500/25">
                    <UserCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-serif font-semibold tracking-tight">
                      My Profile
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      How members see you across the app
                    </p>
                  </div>
                </div>
                <Link href="/settings" data-testid="link-edit-profile">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 hover-elevate"
                  >
                    Edit profile
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div
                  className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground"
                  data-testid="text-profile-initial"
                >
                  {(user?.displayName || user?.username || "?")
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p
                    className="text-sm font-medium text-foreground"
                    data-testid="text-profile-name"
                  >
                    {user?.displayName || user?.username}
                  </p>
                  <p
                    className="text-xs text-muted-foreground"
                    data-testid="text-profile-role"
                  >
                    Coach
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== CONTENT STUDIO OVERVIEW ===== */}
      {canView("content_studio_overview") && (
        <section data-testid="section-content-studio-overview">
          <Card data-testid="card-content-studio">
            <div>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/15 to-rose-500/15 dark:from-violet-500/25 dark:to-rose-500/25">
                      <Activity className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-serif font-semibold tracking-tight">
                        Content Studio
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Overview of your content status
                      </p>
                    </div>
                  </div>
                  <Link
                    href="/content-operations"
                    data-testid="link-content-operations"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 hover-elevate"
                    >
                      Content Operations
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {contentOverviewLoading ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="h-20 bg-muted/20 rounded-xl animate-pulse"
                      />
                    ))}
                  </div>
                ) : contentOverview ? (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Link
                      href="/content-operations"
                      data-testid="link-studio-total"
                    >
                      <div className="group relative p-4 rounded-xl border bg-card/50 hover-elevate cursor-pointer">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2 h-2 rounded-full bg-violet-500" />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Total
                          </span>
                        </div>
                        <p
                          className="text-2xl font-semibold tabular-nums text-foreground"
                          data-testid="text-studio-total"
                        >
                          {contentOverview.totalContent}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {contentOverview.statusCounts.published || 0}{" "}
                          published
                          {contentOverview.statusCounts.failed > 0 && (
                            <span
                              className="text-destructive"
                              data-testid="text-studio-failed"
                            >
                              {" "}
                              · {contentOverview.statusCounts.failed} failed
                            </span>
                          )}
                        </p>
                      </div>
                    </Link>

                    <Link
                      href="/studio/review"
                      data-testid="link-studio-review"
                    >
                      <div
                        className={cn(
                          "group relative p-4 rounded-xl border hover-elevate cursor-pointer",
                          contentOverview.approvalPressure.pendingReview > 0
                            ? "bg-amber-50/50 dark:bg-amber-950/20 border-amber-200/60 dark:border-amber-800/40"
                            : "bg-card/50",
                        )}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              contentOverview.approvalPressure.pendingReview > 0
                                ? "bg-amber-500"
                                : "bg-emerald-500",
                            )}
                          />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Review
                          </span>
                        </div>
                        <p
                          className={cn(
                            "text-2xl font-semibold tabular-nums",
                            contentOverview.approvalPressure.pendingReview > 0
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-foreground",
                          )}
                          data-testid="text-studio-review"
                        >
                          {contentOverview.approvalPressure.pendingReview}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {contentOverview.approvalPressure.pendingReview > 0
                            ? "pending"
                            : "all clear"}
                        </p>
                      </div>
                    </Link>

                    <Link
                      href="studio/quality"
                      data-testid="link-studio-quality"
                    >
                      <div
                        className={cn(
                          "group relative p-4 rounded-xl border hover-elevate cursor-pointer",
                          contentOverview.systemAlerts.total > 0
                            ? "bg-rose-50/50 dark:bg-rose-950/20 border-rose-200/60 dark:border-rose-800/40"
                            : "bg-card/50",
                        )}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              contentOverview.systemAlerts.total > 0
                                ? "bg-rose-400"
                                : "bg-emerald-500",
                            )}
                          />
                          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Quality
                          </span>
                        </div>
                        <p
                          className={cn(
                            "text-2xl font-semibold tabular-nums",
                            contentOverview.systemAlerts.total > 0
                              ? "text-rose-600 dark:text-rose-400"
                              : "text-foreground",
                          )}
                          data-testid="text-studio-quality"
                        >
                          {contentOverview.systemAlerts.total}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {contentOverview.systemAlerts.total > 0
                            ? "issues found"
                            : "all clear"}
                        </p>
                      </div>
                    </Link>

                    <div className="relative p-4 rounded-xl border bg-card/50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Drafts
                        </span>
                      </div>
                      <p
                        className="text-2xl font-semibold tabular-nums text-foreground"
                        data-testid="text-studio-drafts"
                      >
                        {contentOverview.statusCounts.draft || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        in progress
                      </p>
                    </div>
                  </div>
                ) : contentOverviewError ? (
                  <DashboardErrorTile
                    title="content overview"
                    onRetry={() => refetchContentOverview()}
                    isForbidden={
                      (contentOverviewError as ApiError).status === 403
                    }
                  />
                ) : null}
              </CardContent>
            </div>
          </Card>
        </section>
      )}

      {/* ===== REVENUE & CHANNELS (Super Admin Only) ===== */}
      {canView("revenue_chart") &&
        roleConfig.canViewRevenue &&
        (() => {
          // X-axis ticks: ~7-day spacing on daily buckets. Cap visible ticks
          // at ~12 for very long alltime spans by escalating to multi-week
          // intervals proportionally.
          const pickInterval = (count: number) => {
            if (count <= 14) return 0; // small ranges: show every day
            return Math.max(6, Math.floor(count / 12) - 1);
          };
          const pointCount =
            channelsData?.mobile.dataPoints.length ??
            channelsData?.affiliate.dataPoints.length ??
            channelsData?.commissions.dataPoints.length ??
            0;
          const xInterval = pickInterval(pointCount);
          const granularity = channelsData?.granularity ?? "day";
          const fmtTick = (label: string) => {
            try {
              const d = parseISO(label);
              if (granularity === "month") return format(d, "MMM yyyy");
              return format(d, "MMM dd"); // day + week → "Feb 07"
            } catch {
              return label;
            }
          };
          const fmtUsd = (v: number) => `$${v.toLocaleString()}`;
          const fmtAxis = (v: number) =>
            v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`;
          const tooltipBase = {
            backgroundColor: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: "12px",
          };
          const chartMargin = { top: 4, right: 4, left: 0, bottom: 0 };

          const renderStackedAreaChart = (
            data: { date: string; monthly: number; yearly: number }[],
            gradientId: string,
            monthlyColor: string,
            yearlyColor: string,
            interval: number,
          ) => (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={chartMargin}>
                <defs>
                  <linearGradient
                    id={`${gradientId}-yearly`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={yearlyColor}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="95%"
                      stopColor={yearlyColor}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  <linearGradient
                    id={`${gradientId}-monthly`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={monthlyColor}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="95%"
                      stopColor={monthlyColor}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  interval={interval}
                  tickFormatter={fmtTick}
                  tickMargin={6}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickFormatter={fmtAxis}
                  tickCount={4}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <Tooltip
                  contentStyle={tooltipBase}
                  formatter={(value: number, name: string) => [
                    fmtUsd(value),
                    name,
                  ]}
                />
                <Legend
                  wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
                  iconType="circle"
                  height={24}
                />
                <Area
                  type="monotone"
                  dataKey="yearly"
                  name="Yearly"
                  stackId="1"
                  stroke={yearlyColor}
                  strokeWidth={2}
                  fill={`url(#${gradientId}-yearly)`}
                />
                <Area
                  type="monotone"
                  dataKey="monthly"
                  name="Monthly"
                  stackId="1"
                  stroke={monthlyColor}
                  strokeWidth={2}
                  fill={`url(#${gradientId}-monthly)`}
                />
              </AreaChart>
            </ResponsiveContainer>
          );

          const renderCommissionsChart = (
            data: {
              date: string;
              amount: number;
              percentOfAffiliate: number;
            }[],
            interval: number,
          ) => (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={chartMargin}>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  interval={interval}
                  tickFormatter={fmtTick}
                  tickMargin={6}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickFormatter={fmtAxis}
                  tickCount={4}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  width={36}
                  tickFormatter={(v: number) => `${v}%`}
                  domain={[0, "dataMax + 5"]}
                  tickCount={4}
                  tickMargin={4}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                />
                <Tooltip
                  contentStyle={tooltipBase}
                  formatter={(value: number, name: string) =>
                    name === "% of affiliate revenue"
                      ? [`${value}%`, name]
                      : [fmtUsd(value), name]
                  }
                />
                <Legend
                  wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
                  iconType="circle"
                  height={24}
                />
                <Bar
                  yAxisId="left"
                  dataKey="amount"
                  name="Commission paid"
                  fill="hsl(340, 55%, 55%)"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="percentOfAffiliate"
                  name="% of affiliate revenue"
                  stroke="hsl(260, 45%, 55%)"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          );

          const mobileColors = {
            yearly: "hsl(150, 50%, 45%)",
            monthly: "hsl(220, 65%, 60%)",
          };
          const affiliateColors = {
            yearly: "hsl(35, 80%, 55%)",
            monthly: "hsl(340, 65%, 60%)",
          };

          return (
            <section
              className="space-y-4"
              data-testid="section-revenue-channels"
            >
              {/* Section header with shared period selector */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2
                    className="text-xl font-medium flex items-center gap-2"
                    data-testid="text-channels-title"
                  >
                    <DollarSign className="h-4 w-4 text-emerald-500" />
                    Revenue & Channels
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Mobile in-app subs, affiliate (web) subs, and influencer
                    commissions
                  </p>
                </div>
                <Select
                  value={channelsPeriod}
                  onValueChange={setChannelsPeriod}
                >
                  <SelectTrigger
                    className="w-[140px] h-9 px-3 py-2 text-sm"
                    data-testid="select-channels-period"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7days">Last 7 days</SelectItem>
                    <SelectItem value="30days">Last 30 days</SelectItem>
                    <SelectItem value="alltime">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Row 1: Mobile + Affiliate side by side */}
              <div className="grid lg:grid-cols-2 gap-4">
                {/* Mobile Subscriptions */}
                <Card data-testid="card-mobile-subs">
                  <CardHeader className="px-4 pt-4 pb-2 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                          <Smartphone className="h-4 w-4 text-emerald-500" />
                          Mobile App Subscriptions
                        </CardTitle>
                        {channelsData && !channelsLoading && (
                          <div className="mt-1.5">
                            <p
                              className="text-2xl font-semibold text-foreground leading-none"
                              data-testid="text-mobile-total"
                            >
                              {fmtUsd(channelsData.mobile.total)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Yearly {fmtUsd(channelsData.mobile.totalYearly)} ·
                              Monthly {fmtUsd(channelsData.mobile.totalMonthly)}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {channelsData &&
                          !channelsLoading &&
                          channelsData.mobile.trend !== null && (
                            <TrendBadge value={channelsData.mobile.trend} />
                          )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setChannelsFullscreen("mobile")}
                          data-testid="button-mobile-fullscreen"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-2 pb-3 pt-0">
                    {channelsLoading ? (
                      <div className="h-48 bg-muted/20 rounded-lg animate-pulse" />
                    ) : channelsData ? (
                      <div className="h-48">
                        {renderStackedAreaChart(
                          channelsData.mobile.dataPoints,
                          "mobileGrad",
                          mobileColors.monthly,
                          mobileColors.yearly,
                          xInterval,
                        )}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>

                {/* Affiliate Subscriptions */}
                <Card data-testid="card-affiliate-subs">
                  <CardHeader className="px-4 pt-4 pb-2 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                          <Users className="h-4 w-4 text-amber-500" />
                          Affiliate (Web) Subscriptions
                        </CardTitle>
                        {channelsData && !channelsLoading && (
                          <div className="mt-1.5">
                            <p
                              className="text-2xl font-semibold text-foreground leading-none"
                              data-testid="text-affiliate-total"
                            >
                              {fmtUsd(channelsData.affiliate.total)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Yearly{" "}
                              {fmtUsd(channelsData.affiliate.totalYearly)} ·
                              Monthly{" "}
                              {fmtUsd(channelsData.affiliate.totalMonthly)}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {channelsData &&
                          !channelsLoading &&
                          channelsData.affiliate.trend !== null && (
                            <TrendBadge value={channelsData.affiliate.trend} />
                          )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setChannelsFullscreen("affiliate")}
                          data-testid="button-affiliate-fullscreen"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-2 pb-3 pt-0">
                    {channelsLoading ? (
                      <div className="h-48 bg-muted/20 rounded-lg animate-pulse" />
                    ) : channelsData ? (
                      <div className="h-48">
                        {renderStackedAreaChart(
                          channelsData.affiliate.dataPoints,
                          "affiliateGrad",
                          affiliateColors.monthly,
                          affiliateColors.yearly,
                          xInterval,
                        )}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>

              {/* Row 2: Influencer Commissions full width */}
              <Card data-testid="card-commissions">
                <CardHeader className="px-4 pt-4 pb-2 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <Banknote className="h-4 w-4 text-rose-500" />
                        Influencer Commissions
                      </CardTitle>
                      {channelsData && !channelsLoading && (
                        <div className="mt-1.5">
                          <p
                            className="text-2xl font-semibold text-foreground leading-none"
                            data-testid="text-commissions-total"
                          >
                            {fmtUsd(channelsData.commissions.total)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Avg {channelsData.commissions.avgPercent}% of
                            affiliate revenue
                          </p>
                          {channelsData.commissions.total === 0 &&
                            channelsData.commissions.lifetimeTotal > 0 && (
                              <p
                                className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                                data-testid="text-commissions-lifetime-hint"
                              >
                                No commissions in this period · Lifetime{" "}
                                {fmtUsd(channelsData.commissions.lifetimeTotal)}{" "}
                                ({channelsData.commissions.lifetimeCount} tx).
                                Try All time.
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {channelsData &&
                        !channelsLoading &&
                        channelsData.commissions.trend !== null && (
                          <TrendBadge value={channelsData.commissions.trend} />
                        )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setChannelsFullscreen("commissions")}
                        data-testid="button-commissions-fullscreen"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-2 pb-3 pt-0">
                  {channelsLoading ? (
                    <div className="h-44 bg-muted/20 rounded-lg animate-pulse" />
                  ) : channelsData ? (
                    <div className="h-44">
                      {renderCommissionsChart(
                        channelsData.commissions.dataPoints,
                        xInterval,
                      )}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {/* Fullscreen dialog (shared, switches based on which chart was clicked) */}
              <Dialog
                open={channelsFullscreen !== null}
                onOpenChange={(o) => !o && setChannelsFullscreen(null)}
              >
                <DialogContent className="max-w-6xl h-[80vh]">
                  <DialogHeader className="pr-8">
                    <div className="flex items-center justify-between gap-4">
                      <DialogTitle className="flex items-center gap-2">
                        {channelsFullscreen === "mobile" && (
                          <Smartphone className="h-5 w-5 text-emerald-500" />
                        )}
                        {channelsFullscreen === "affiliate" && (
                          <Users className="h-5 w-5 text-amber-500" />
                        )}
                        {channelsFullscreen === "commissions" && (
                          <Banknote className="h-5 w-5 text-rose-500" />
                        )}
                        {channelsFullscreen === "mobile" &&
                          "Mobile App Subscriptions"}
                        {channelsFullscreen === "affiliate" &&
                          "Affiliate (Web) Subscriptions"}
                        {channelsFullscreen === "commissions" &&
                          "Influencer Commissions"}
                      </DialogTitle>
                      <Select
                        value={channelsPeriod}
                        onValueChange={setChannelsPeriod}
                      >
                        <SelectTrigger
                          className="w-[140px] h-9 px-3 py-2 text-sm"
                          data-testid="select-channels-period-fullscreen"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7days">Last 7 days</SelectItem>
                          <SelectItem value="30days">Last 30 days</SelectItem>
                          <SelectItem value="alltime">All time</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </DialogHeader>
                  <div className="flex-1 mt-4">
                    {channelsLoading ? (
                      <div className="h-full bg-muted/20 rounded-lg animate-pulse" />
                    ) : channelsData && channelsFullscreen === "mobile" ? (
                      <div className="h-full">
                        {renderStackedAreaChart(
                          channelsData.mobile.dataPoints,
                          "mobileGradFull",
                          mobileColors.monthly,
                          mobileColors.yearly,
                          pickInterval(channelsData.mobile.dataPoints.length),
                        )}
                      </div>
                    ) : channelsData && channelsFullscreen === "affiliate" ? (
                      <div className="h-full">
                        {renderStackedAreaChart(
                          channelsData.affiliate.dataPoints,
                          "affiliateGradFull",
                          affiliateColors.monthly,
                          affiliateColors.yearly,
                          pickInterval(
                            channelsData.affiliate.dataPoints.length,
                          ),
                        )}
                      </div>
                    ) : channelsData && channelsFullscreen === "commissions" ? (
                      <div className="h-full">
                        {renderCommissionsChart(
                          channelsData.commissions.dataPoints,
                          pickInterval(
                            channelsData.commissions.dataPoints.length,
                          ),
                        )}
                      </div>
                    ) : null}
                  </div>
                </DialogContent>
              </Dialog>
            </section>
          );
        })()}

      {/* ===== CARE QUEUE removed per product request ===== */}

      {/* ===== PENDING REVIEWS (Primary for Content Manager) ===== */}
      {canView("pending_reviews") && (
        <section data-testid="section-pending-reviews">
          <Card data-testid="card-pending-reviews">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xl font-medium flex items-center gap-2">
                  <FileCheck className="h-4 w-4 text-amber-500" />
                  Pending Reviews
                </CardTitle>
                <Link href="/content?status=submitted">
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">
                    View All <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="list-pending-reviews">
                {pendingContent.map((item) => (
                  <Link
                    key={item.id}
                    href={`/content/${item.id}`}
                    data-testid={`link-review-${item.id}`}
                  >
                    <div className="flex items-center gap-3 p-3 rounded-lg border hover-elevate cursor-pointer">
                      <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                        <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          by {item.coachName} -{" "}
                          {formatDistanceToNow(new Date(item.submittedAt), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {item.type}
                      </Badge>
                      <ArrowRight className="h-4 w-4 text-muted-foreground/50" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== COACH ACTIVITY (Primary for Coach Manager) ===== */}
      {canView("coach_activity") && (
        <section data-testid="section-coach-activity">
          <Card data-testid="card-coach-activity">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xl font-medium flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-violet-500" />
                  Coach Activity Feed
                </CardTitle>
                <Link href="/coaches">
                  <Button variant="ghost" size="sm" className="gap-1 text-xs">
                    All Coaches <ArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="list-coach-activity">
                {coachActivity.map((item) => {
                  const statusConfig = {
                    active: {
                      bg: "bg-emerald-100 dark:bg-emerald-900/30",
                      text: "text-emerald-600 dark:text-emerald-400",
                    },
                    onboarding: {
                      bg: "bg-blue-100 dark:bg-blue-900/30",
                      text: "text-blue-600 dark:text-blue-400",
                    },
                    needs_attention: {
                      bg: "bg-rose-100 dark:bg-rose-900/30",
                      text: "text-rose-600 dark:text-rose-400",
                    },
                  }[item.status];

                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 rounded-lg border"
                    >
                      <div className={cn("p-2 rounded-lg", statusConfig.bg)}>
                        <UserCheck
                          className={cn("h-4 w-4", statusConfig.text)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {item.coachName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {item.action}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(item.timestamp), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== ENGAGEMENT CHARTS removed per product request (Weekly Engagement Flow + Ritual Metrics) ===== */}

      {/* ===== SUBSCRIPTION HEALTH (Super Admin & Admin only) ===== */}
      {canView("subscription_health") && roleConfig.canViewSubscriptions && (
        <section data-testid="section-business">
          <Card data-testid="card-membership-trend">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-emerald-500" />
                Subscription Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptionLoading ? (
                <div className="h-40 bg-muted/20 rounded-lg animate-pulse" />
              ) : subscriptionHealth?.membershipTrend ? (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={subscriptionHealth.membershipTrend}>
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fontSize: 11,
                          fill: "hsl(var(--muted-foreground))",
                        }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fontSize: 11,
                          fill: "hsl(var(--muted-foreground))",
                        }}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                      />
                      <Bar
                        dataKey="active"
                        fill="hsl(150, 45%, 50%)"
                        radius={[4, 4, 0, 0]}
                        name="Active"
                      />
                      <Bar
                        dataKey="trial"
                        fill="hsl(35, 55%, 55%)"
                        radius={[4, 4, 0, 0]}
                        name="Trial"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== CONTENT PERFORMANCE (Content Manager) ===== */}
      {canView("content_performance") && (
        <section
          className="grid lg:grid-cols-2 gap-6"
          data-testid="section-content-performance"
        >
          <Card data-testid="card-content-coverage">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-violet-500" />
                Content Coverage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { type: "Audio", coverage: 85, color: "bg-violet-400" },
                  { type: "Video", coverage: 62, color: "bg-rose-400" },
                  { type: "Journey", coverage: 78, color: "bg-emerald-400" },
                  { type: "Blog", coverage: 45, color: "bg-amber-400" },
                ].map((item) => (
                  <div key={item.type} className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        {item.type}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {item.coverage}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          item.color,
                        )}
                        style={{ width: `${item.coverage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-recent-published">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                Recently Published
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  {
                    title: "Calm Morning Ritual",
                    type: "audio",
                    publishedAt: "2h ago",
                  },
                  {
                    title: "Stress Relief Journey",
                    type: "journey",
                    publishedAt: "5h ago",
                  },
                  {
                    title: "Evening Reflection",
                    type: "audio",
                    publishedAt: "1d ago",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                  >
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">
                        {item.title}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {item.type}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {item.publishedAt}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== COACH QUALITY (Coach Manager) ===== */}
      {canView("coach_quality") && (
        <section
          className="grid lg:grid-cols-2 gap-6"
          data-testid="section-coach-quality"
        >
          <Card data-testid="card-coach-engagement">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-violet-500" />
                Coach Engagement Signals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: "Avg. Content Quality Score", value: "4.2/5" },
                  { label: "Avg. Response Time", value: "< 24h" },
                  { label: "Active Submission Rate", value: "72%" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/30"
                  >
                    <span className="text-sm text-muted-foreground">
                      {item.label}
                    </span>
                    <span className="text-sm font-semibold text-foreground">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-coaches-needing-attention">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xl font-medium flex items-center gap-2">
                  <Heart className="h-4 w-4 text-rose-500" />
                  Coaches Needing Attention
                </CardTitle>
                <Badge variant="secondary" className="text-xs">
                  {coachManagerStats.coachesNeedingAttention}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  {
                    name: "Maria Santos",
                    reason: "No submissions in 14 days",
                    lastActive: "14d ago",
                  },
                  {
                    name: "Emily Rose",
                    reason: "Profile incomplete",
                    lastActive: "3d ago",
                  },
                ].map((coach, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30"
                  >
                    <AlertCircle className="h-4 w-4 text-rose-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {coach.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {coach.reason}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {coach.lastActive}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== STANDALONE RECENT ACTIVITY (Care Queue removed; show for all roles that can view activity) ===== */}
      {canView("recent_activity") && (
        <section data-testid="section-standalone-activity">
          <Card data-testid="card-standalone-activity">
            <CardHeader className="pb-3">
              <CardTitle className="text-xl font-medium flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activitiesLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="h-12 bg-muted/20 rounded animate-pulse"
                    />
                  ))}
                </div>
              ) : activities && activities.length > 0 ? (
                <div
                  className="grid gap-2 md:grid-cols-2"
                  data-testid="list-standalone-activity"
                >
                  {activities.slice(0, 6).map((activity) => {
                    const Icon = activityIcons[activity.type] || FileText;
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                        data-testid={`row-standalone-activity-${activity.id}`}
                      >
                        <div className="p-1.5 rounded bg-muted/50 shrink-0">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">
                            {activity.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(activity.timestamp), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent activity
                </p>
              )}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
